const TRANSFER_RULE_KEYS = {
  internal: ["INTERNAL_TRANSFER", "INTERNAL_TRASFER"],
  external: ["EXTERNAL_TRANSFER", "EXTERNAL_TRASFER"],
};

const APPROVAL_ROLE_LABELS = {
  FRONT_DESK: "Front Desk",
  BILLING: "Billing",
  INVENTORY_MANAGER: "Inventory Manager",
  BRANCH_MANAGER: "Practice Manager",
  CLINIC_OWNER: "Clinic Owner",
  ROLE_CLINICSUPERADMIN: "Super Admin",
};

const normalizeRequestType = (requestTypeValue) =>
  String(requestTypeValue ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");

const getTransferRuleKeys = (requestTypeValue) => {
  const normalized = normalizeRequestType(requestTypeValue);
  if (normalized === "internaltransfer" || normalized === "internaltrasfer") {
    return TRANSFER_RULE_KEYS.internal;
  }
  if (normalized === "externaltransfer" || normalized === "externaltrasfer") {
    return TRANSFER_RULE_KEYS.external;
  }
  return null;
};

export const getTransferRequestType = (requestTypeValue) => {
  const ruleKeys = getTransferRuleKeys(requestTypeValue);
  return ruleKeys?.[0] ?? null;
};

const getTransferRuleConfig = (approvalRules, requestTypeValue) => {
  const ruleKeys = getTransferRuleKeys(requestTypeValue);
  if (!ruleKeys) return null;

  const rulesMap = approvalRules?.rules ?? approvalRules;
  if (!rulesMap || typeof rulesMap !== "object") return null;

  return ruleKeys.map((key) => rulesMap[key]).find(Boolean) ?? null;
};

export const getTransferMatchedRule = (approvalRules, requestTypeValue) => {
  const config = getTransferRuleConfig(approvalRules, requestTypeValue);
  const rules = Array.isArray(config?.rules) ? config.rules : [];
  return rules[0] ?? null;
};

export const buildTransferApprovalStep = (rule) => {
  if (!rule) return "";
  const tierNo = Number(rule?.sortOrder ?? 0) + 1;
  const roleCode = Array.isArray(rule?.roles) ? rule.roles[0] : null;
  const roleLabel = APPROVAL_ROLE_LABELS[roleCode] || roleCode || "";
  return [`Tier ${tierNo}`, roleLabel].filter(Boolean).join(" · ");
};

/**
 * Returns whether a stock transfer should be auto-approved.
 * - autoApproval true or rule not found → true
 * - autoApproval false → false
 * - non-transfer request types (e.g. Consumption) → true
 */
export const isTransferAutoApproved = (approvalRules, requestTypeValue) => {
  const config = getTransferRuleConfig(approvalRules, requestTypeValue);
  if (!getTransferRuleKeys(requestTypeValue)) return true;
  if (!config) return true;

  return !!config.autoApproval;
};

/**
 * COUNT_CORRECTION auto-approval for stock adjustments.
 * - autoApproval true or rule not found → true
 * - autoApproval false → false
 */
export const isCountCorrectionAutoApproved = (approvalRules) => {
  const rulesMap = approvalRules?.rules;
  if (!rulesMap || typeof rulesMap !== "object") return true;

  const config = rulesMap.COUNT_CORRECTION;
  if (!config) return true;

  return !!config.autoApproval;
};

export const getCountCorrectionApproveFlag = (approvalRules) =>
  isCountCorrectionAutoApproved(approvalRules) ? "Y" : "N";

export const getCountCorrectionMatchedRule = (approvalRules) => {
  const rulesMap = approvalRules?.rules ?? approvalRules;
  if (!rulesMap || typeof rulesMap !== "object") return null;

  const config = rulesMap.COUNT_CORRECTION;
  const rules = Array.isArray(config?.rules) ? config.rules : [];
  return rules[0] ?? null;
};

/**
 * SERVICE_NEW / INVENTORY_NEW auto-approval.
 * - editing existing (has id) → true
 * - autoApproval true or rule not found → true
 * - otherwise → false
 */
export const isMasterItemAutoApproved = (
  approvalRules,
  ruleType,
  { isEdit = false } = {},
) => {
  if (isEdit) return true;

  const rulesMap = approvalRules?.rules;
  if (!rulesMap || typeof rulesMap !== "object") return true;

  const config = rulesMap[ruleType];
  if (!config) return true;

  return !!config.autoApproval;
};
